import { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";

//Component
import LeftNavigationBar from "components/LeftNavigationBar";
// Routes
import PageRoutes from "routes/page-routes";
import { useBreadcrumb } from "context/breadcrumb.context";
import { useAppState } from "context/appState.context";
import { useNavigate } from "react-router-dom";

type ViewMode = "grid" | "list";

/**
 * LocationLayout component is the main layout for the location section.
 * It includes a left navigation bar, a toggle button for the summary bar, and the main content area for page routes.
 *
 * @component
 * @returns {JSX.Element} The rendered component.
 */
export default function LocationLayout(): JSX.Element {
  // Hide and Show Summary Bar
  const [showSummaryBar, setShowSummaryBar] = useState<boolean>(false);
  const { setBreadcrumbs } = useBreadcrumb();
  const { locationName } = useAppState();
  const navigate = useNavigate();

  // Set breadcrumbs: Client → Organization → Location
  useEffect(() => {
    const clientName = localStorage.getItem("breadcrumb_clientName");
    const clientId = localStorage.getItem("breadcrumb_clientId");
    const orgName = localStorage.getItem("breadcrumb_orgName");
    const orgId = localStorage.getItem("breadcrumb_orgId");
    const resolvedLocationName = locationName ?? localStorage.getItem("breadcrumb_locationName") ?? "";
    const storedViewMode = localStorage.getItem("clientDetailViewMode");
    const viewMode = storedViewMode === "grid" || storedViewMode === "list"
      ? (storedViewMode as ViewMode)
      : undefined;
    const parsedClientId = clientId != null ? Number(clientId) : undefined;
    const parsedOrgId = orgId != null ? Number(orgId) : undefined;
    const items: { label: string; onClick?: () => void; isActive?: boolean }[] = [];

    if (clientName) {
      items.push({
        label: clientName,
        isActive: false,
        onClick: () =>
          navigate(viewMode === "list" ? "/Clients" : "/Organizations", {
            state: viewMode === "list"
              ? {
                  clientId: parsedClientId,
                  viewLevel: "client",
                  viewMode,
                  expandedClientId: parsedClientId,
                }
              : {
                  clientId: parsedClientId,
                  viewLevel: "organization",
                  viewMode,
                },
          }),
      });
    }

    if (orgName) {
      items.push({
        label: orgName,
        isActive: false,
        onClick: () =>
          navigate(viewMode === "list" ? "/Clients" : "/Locations", {
            state: viewMode === "list"
              ? {
                  clientId: parsedClientId,
                  orgId: parsedOrgId,
                  viewLevel: "client",
                  viewMode,
                  expandedClientId: parsedClientId,
                  expandedOrgId: parsedOrgId,
                }
              : {
                  clientId: parsedClientId,
                  orgId: parsedOrgId,
                  viewLevel: "location",
                  viewMode,
                },
          }),
      });
    }

    if (resolvedLocationName) {
      items.push({
        label: resolvedLocationName,
        isActive: true,
      });
    }

    setBreadcrumbs(items);

    return () => setBreadcrumbs([]);
  }, [locationName, setBreadcrumbs, navigate]);

  return (
    <Box sx={{ display: "flex", width: "100%", overflowX: "hidden" }} style={{ marginTop: "0px" }}>
      {/* Left Navigation Bar */}
      <LeftNavigationBar />

      {/* Center Component */}
      <Box component="main" sx={{ flexGrow: 1, minWidth: 0, overflowX: "hidden" }}>
        {/* <Grid container spacing={0}> */}
          <Grid item xs={12} md={12} lg={12} style={{ marginTop: "10px" }}>
            {/* Pages */}
            <PageRoutes />
          </Grid>
        {/* </Grid> */}
      </Box>
    </Box>
  );
}
